import { computed, signal } from '@zyra/core';
export type Entity = Record<string, unknown>;
export type SyncStatus = 'local' | 'pending' | 'syncing' | 'synced' | 'conflicted' | 'rejected' | 'storage-error';
export interface Mutation<T> {
  mutationId: string; clientId: string; collection: string; entityId: string;
  operation: 'put' | 'delete'; payload?: T; baseVersion: number; createdAt: number;
  retryState: 'pending' | 'conflict' | 'rejected'; reason?: string; server?: T | null; serverVersion?: number;
}
export interface Snapshot<T> {
  schema: number; items: T[]; journal: Mutation<T>[]; versions: Record<string, number>; cursor: string; revision?: number; owner?: string; sessionEpoch?: string;
}
export interface StorageAdapter {
  load<T>(name: string): Promise<Snapshot<T> | undefined>;
  save<T>(name: string, value: Snapshot<T>): Promise<void>;
  /** Synchronous read-modify-write callback within the storage engine's transaction. */
  update?<T>(name: string, change: (current: Snapshot<T> | undefined) => Snapshot<T>): Promise<Snapshot<T>>;
  subscribe?(name: string, changed: () => void): () => void;
  close?(): void;
}
export function memoryStorage(): StorageAdapter {
  const values = new Map<string, unknown>();
  const listeners = new Map<string, Set<() => void>>();
  const notify = (name: string) => queueMicrotask(() => listeners.get(name)?.forEach(fn => fn()));
  return {
    async load<T>(name: string) { return structuredClone(values.get(name)) as Snapshot<T> | undefined; },
    async save(name, value) { values.set(name, structuredClone(value)); notify(name); },
    async update<T>(name: string, change: (current: Snapshot<T> | undefined) => Snapshot<T>) {
      const next = change(structuredClone(values.get(name)) as Snapshot<T> | undefined);
      values.set(name, structuredClone(next)); notify(name); return structuredClone(next);
    },
    subscribe(name, changed) {
      let set = listeners.get(name); if (!set) listeners.set(name, set = new Set()); set.add(changed);
      return () => { set.delete(changed); if (!set.size) listeners.delete(name); };
    }
  };
}
const storageEvents = new Map<string, Set<(name: string) => void>>();
export function indexedDBStorage(database = 'zyra'): StorageAdapter {
  let connection: Promise<IDBDatabase> | undefined;
  let closed = false;
  const listeners = new Map<string, Set<() => void>>();
  const localListeners = storageEvents.get(database) ?? new Set<(name: string) => void>();
  storageEvents.set(database, localListeners);
  const notifyLocal = (name: string) => listeners.get(name)?.forEach(fn => fn());
  localListeners.add(notifyLocal);
  // Local dispatch also covers environments without BroadcastChannel. IDB transactions provide
  // write safety independently of notifications, including across processes and browser tabs.
  const channel = typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(`zyra:idb:${database}`);
  (channel as (BroadcastChannel & { unref?: () => void }) | undefined)?.unref?.();
  channel?.addEventListener('message', event => { if (typeof event.data === 'string') notifyLocal(event.data); });
  const notify = (name: string) => {
    if (closed) return;
    localListeners.forEach(fn => fn(name)); channel?.postMessage(name);
  };
  const open = () => connection ??= new Promise((resolve, reject) => {
    if (closed) { reject(new Error('Storage adapter is closed')); return; }
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB unavailable')); return; }
    const request = indexedDB.open(database, 1);
    request.onupgradeneeded = () => request.result.createObjectStore('collections');
    request.onerror = () => reject(request.error);
    let settled = false;
    request.onblocked = () => { settled = true; connection = undefined; reject(new Error('IndexedDB upgrade blocked by another tab')); };
    request.onsuccess = () => {
      const db = request.result;
      if (settled || closed) { db.close(); return; }
      db.onversionchange = () => { db.close(); connection = undefined; };
      resolve(db);
    };
  });
  return {
    async load<T>(name: string) {
      const db = await open();
      return new Promise<Snapshot<T> | undefined>((resolve, reject) => {
        const tx = db.transaction('collections', 'readonly');
        const request = tx.objectStore('collections').get(name);
        tx.oncomplete = () => resolve(request.result);
        tx.onerror = tx.onabort = () => reject(tx.error ?? new Error('Storage read aborted'));
      });
    },
    async save(name, value) {
      const db = await open();
      return new Promise<void>((resolve, reject) => {
        const tx = db.transaction('collections', 'readwrite');
        tx.objectStore('collections').put(structuredClone(value), name);
        tx.oncomplete = () => { resolve(); notify(name); };
        tx.onerror = tx.onabort = () => reject(tx.error ?? new Error('Storage commit aborted'));
      });
    },
    async update<T>(name: string, change: (current: Snapshot<T> | undefined) => Snapshot<T>) {
      const db = await open();
      return new Promise<Snapshot<T>>((resolve, reject) => {
        const tx = db.transaction('collections', 'readwrite');
        const store = tx.objectStore('collections');
        let next: Snapshot<T>, failure: unknown;
        const request = store.get(name);
        request.onsuccess = () => {
          try { next = change(request.result); store.put(next, name); }
          catch (error) { failure = error; tx.abort(); }
        };
        tx.oncomplete = () => { resolve(next); notify(name); };
        tx.onerror = tx.onabort = () => reject(failure ?? tx.error ?? new Error('Storage update aborted'));
      });
    },
    subscribe(name, changed) {
      const set = listeners.get(name) ?? new Set<() => void>(); listeners.set(name, set); set.add(changed);
      return () => { set.delete(changed); if (!set.size) listeners.delete(name); };
    },
    close() {
      closed = true; channel?.close(); listeners.clear(); localListeners.delete(notifyLocal);
      if (!localListeners.size) storageEvents.delete(database);
      void connection?.then(db => db.close(), () => {});
    }
  };
}
export interface CollectionOptions<T> {
  key: keyof T; local?: 'indexeddb' | 'memory'; database?: string; storage?: StorageAdapter;
  version?: number; validate?: (item: unknown) => item is T;
  migrate?: (items: unknown[], fromVersion: number) => T[];
  storageFallback?: 'error' | 'memory';
  /** Stable authenticated subject; automatically partitions the local cache. */
  owner?: string;
  sync?: { endpoint: string; collection?: string }; conflict?: 'manual' | 'server-wins';
}
export function collection<T extends object>(name: string, options: CollectionOptions<T>) {
  if (options.owner !== undefined && (typeof options.owner !== 'string' || !options.owner)) throw new Error('Collection owner must be a non-empty authenticated subject');
  const storageName = options.owner === undefined ? name : JSON.stringify([options.owner, name]);
  let adapter = options.storage ?? (options.local === 'memory' ? memoryStorage() : indexedDBStorage(options.database));
  const schema = options.version ?? 1;
  const state = signal<Snapshot<T>>({ schema, items: [], journal: [], versions: {}, cursor: '', owner: options.owner });
  const loading = signal(true), degraded = signal(options.local === 'memory'), syncing = signal(false), error = signal<string | null>(null);
  let closed = false, closing = false, initializationError: unknown, volatile = options.local === 'memory';
  const id = (item: T) => {
    const value = item[options.key];
    if ((typeof value !== 'string' && typeof value !== 'number') || String(value) === '' || (typeof value === 'number' && !Number.isFinite(value))) throw new Error('Collection keys must be non-empty strings or finite numbers');
    return String(value);
  };
  const validate = (item: T) => { id(item); if (options.validate && !options.validate(item)) throw new Error(`Invalid record in ${name}`); };
  const normalize = (saved: Snapshot<T>): Snapshot<T> => {
    if (!saved || !Array.isArray(saved.items) || !Array.isArray(saved.journal) || !saved.versions || typeof saved.versions !== 'object' || typeof saved.cursor !== 'string' || !Number.isSafeInteger(saved.schema)) throw new Error('Invalid collection snapshot');
    if (saved.owner !== options.owner) throw new Error('Collection belongs to a different identity');
    if (saved.sessionEpoch !== undefined && typeof saved.sessionEpoch !== 'string') throw new Error('Invalid session epoch');
    if (saved.revision !== undefined && (!Number.isSafeInteger(saved.revision) || saved.revision < 0)) throw new Error('Invalid collection revision');
    if (saved.schema !== schema) {
      if (saved.schema > schema || !options.migrate) throw new Error(`No migration from schema ${saved.schema} to ${schema}`);
      if (saved.journal.length) throw new Error('Resolve pending mutations before migrating schema');
      saved.items = options.migrate(saved.items, saved.schema); saved.schema = schema;
    }
    for (const item of saved.items) validate(item);
    if (new Set(saved.items.map(id)).size !== saved.items.length) throw new Error('Duplicate stored collection keys');
    for (const version of Object.values(saved.versions)) if (!Number.isSafeInteger(version) || version < 0) throw new Error('Invalid record version');
    for (const entry of saved.journal) {
      if (!entry || typeof entry.mutationId !== 'string' || !entry.mutationId || typeof entry.entityId !== 'string' ||
          !['put', 'delete'].includes(entry.operation) || !['pending', 'conflict', 'rejected'].includes(entry.retryState) ||
          !Number.isSafeInteger(entry.baseVersion) || entry.baseVersion < 0) throw new Error('Invalid mutation journal');
      if (entry.operation === 'put') { validate(entry.payload!); if (id(entry.payload!) !== entry.entityId) throw new Error('Journal key mismatch'); }
      if (typeof entry.clientId !== 'string' || !entry.clientId || !Number.isFinite(entry.createdAt) || entry.collection !== (options.sync?.collection ?? name)) throw new Error('Invalid journal identity');
    }
    if (new Set(saved.journal.map(entry => entry.mutationId)).size !== saved.journal.length) throw new Error('Duplicate mutation IDs in journal');
    return saved;
  };
  const ready = (async () => {
    try {
      const saved = await adapter.load<T>(storageName);
      if (saved) {
        if (saved.schema !== schema && adapter.update) state.value = await adapter.update<T>(storageName, current => normalize(current!));
        else {
          const migrated = saved.schema !== schema;
          const value = normalize(saved);
          if (migrated) await adapter.save(storageName, value);
          state.value = value;
        }
      }
    } catch (cause) {
      // Preserve unknown/corrupt schemas. Do not replace or overwrite an existing database.
      error.value = String(cause); degraded.value = true;
      if (options.storageFallback === 'memory') { adapter.close?.(); adapter = memoryStorage(); volatile = true; }
      else initializationError = cause;
    } finally { loading.value = false; }
  })();
  let tail: Promise<unknown> = ready;
  function commit(change: (draft: Snapshot<T>) => void): Promise<void> {
    if (closing || closed) return Promise.reject(new Error('Collection is closed'));
    const job = tail.then(async () => {
      if (initializationError) throw initializationError;
      let changeError = false;
      const modify = (latest: Snapshot<T> | undefined) => {
        let draft: Snapshot<T>;
        try { draft = normalize(structuredClone(latest ?? state.value)); }
        catch (cause) { initializationError = cause; error.value = String(cause); degraded.value = true; throw cause; }
        try {
        const result: unknown = change(draft);
        if (result && typeof (result as Promise<unknown>).then === 'function') { void Promise.resolve(result).catch(() => {}); throw new Error('Collection transactions must be synchronous'); }
        normalize(draft);
        draft.revision = (draft.revision ?? 0) + 1;
        if (!Number.isSafeInteger(draft.revision)) throw new Error('Collection revision limit exceeded');
        return draft;
        } catch (cause) { changeError = true; throw cause; }
      };
      let draft: Snapshot<T>;
      try {
        if (adapter.update) draft = await adapter.update<T>(storageName, modify);
        else { draft = modify(state.value); await adapter.save(storageName, draft); }
      }
      catch (cause) { if (!changeError) { error.value = String(cause); degraded.value = true; } throw cause; }
      state.value = draft; degraded.value = volatile; if (!volatile) error.value = null;
    });
    tail = job.catch(() => {});
    return job;
  }
  function refresh(): Promise<void> {
    if (closing || closed) return Promise.resolve();
    const job = tail.then(async () => {
      if (initializationError) return;
      const latest = await adapter.load<T>(storageName);
      if (latest && (latest.revision ?? 0) >= (state.value.revision ?? 0)) {
        try { state.value = normalize(latest); }
        catch (cause) { initializationError = cause; throw cause; }
      }
    });
    tail = job.catch(cause => { error.value = String(cause); degraded.value = true; });
    return job;
  }
  let refreshQueued = false;
  const unsubscribe = adapter.subscribe?.(storageName, () => {
    if (refreshQueued || closed || closing) return;
    refreshQueued = true;
    queueMicrotask(() => { refreshQueued = false; void refresh().catch(() => {}); });
  });
  function journal(draft: Snapshot<T>, entityId: string, operation: 'put' | 'delete', payload?: T): void {
    if (!options.sync) return;
    draft.journal.push({ mutationId: crypto.randomUUID(), clientId, collection: options.sync.collection ?? name,
      entityId, operation, payload: payload ? structuredClone(payload) : undefined,
      baseVersion: Object.hasOwn(draft.versions, entityId) ? draft.versions[entityId] : 0, createdAt: Date.now(), retryState: 'pending' });
  }
  // IDs are stable for the lifetime of this collection; mutation IDs persist across restarts.
  const clientId = crypto.randomUUID();
  const status = {
    get loading() { return loading.value; }, get storageDegraded() { return degraded.value; },
    get writable() { return !initializationError && !closing && !closed; },
    get storageMode(): 'persistent' | 'memory' | 'blocked' { return initializationError ? 'blocked' : volatile ? 'memory' : 'persistent'; },
    get syncing() { return syncing.value; }, get error() { return error.value; },
    get pending() { return state.value.journal.filter(entry => entry.retryState === 'pending').length; },
    get conflicted() { return state.value.journal.some(entry => entry.retryState === 'conflict'); },
    get rejected() { return state.value.journal.some(entry => entry.retryState === 'rejected'); },
    get state(): SyncStatus {
      return degraded.value ? 'storage-error' : this.conflicted ? 'conflicted' : this.rejected ? 'rejected'
        : syncing.value ? 'syncing' : this.pending ? 'pending' : options.sync ? 'synced' : 'local';
    }
  };
  return {
    name, options, ready, status,
    get items(): readonly T[] { return structuredClone(state.value.items); },
    get mutations(): readonly Mutation<T>[] { return structuredClone(state.value.journal); },
    snapshot(): Snapshot<T> { return structuredClone(state.value); },
    insert(item: T) {
      const copy = structuredClone(item); validate(copy);
      return commit(draft => { const key = id(copy); if (draft.items.some(row => id(row) === key)) throw new Error(`Duplicate key: ${key}`);
        draft.items.push(copy); journal(draft, key, 'put', copy); });
    },
    update(entityId: string, patch: Partial<T>) {
      const copy = structuredClone(patch);
      return commit(draft => {
        const index = draft.items.findIndex(row => id(row) === entityId);
        if (index < 0) throw new Error(`Record not found: ${entityId}`);
        const next = { ...draft.items[index], ...copy }; validate(next);
        if (id(next) !== entityId) throw new Error('Primary keys cannot be changed');
        draft.items[index] = next; journal(draft, entityId, 'put', next);
      });
    },
    remove(entityId: string) {
      return commit(draft => { if (!draft.items.some(row => id(row) === entityId)) return;
        draft.items = draft.items.filter(row => id(row) !== entityId); journal(draft, entityId, 'delete'); });
    },
    query(predicate: (item: T) => boolean) { return computed(() => state.value.items.filter(predicate).map(item => structuredClone(item))); },
    async clear() { await commit(draft => { draft.items = []; draft.journal = []; draft.versions = {}; draft.cursor = ''; draft.sessionEpoch = crypto.randomUUID(); }); },
    async close() { closing = true; unsubscribe?.(); await tail; closed = true; adapter.close?.(); },
    refresh,
    exportSnapshot() { return JSON.stringify(state.value); },
    async importSnapshot(json: string, options: { replace?: boolean } = {}) {
      const imported = normalize(JSON.parse(json));
      await commit(draft => {
        if (!options.replace && (draft.items.length || draft.journal.length)) throw new Error('Restoring over existing data requires replace: true');
        const revision = draft.revision;
        Object.assign(draft, { schema: imported.schema, items: imported.items, journal: imported.journal, versions: imported.versions, cursor: imported.cursor, revision });
      });
    },
    /** Protocol integration: a single atomic snapshot includes records, journal, versions and cursor. */
    commit, keyOf: id,
    setSyncing(value: boolean) { syncing.value = value; },
    setError(value: string | null) { error.value = value; }
  };
}
export type Collection<T extends object> = ReturnType<typeof collection<T>>;

export async function registerOfflineShell(url = '/sw.js'): Promise<ServiceWorkerRegistration | undefined> {
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) return navigator.serviceWorker.register(url);
}
